import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToolbarComponent } from '../../components/toolbar/toolbar.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { RopaService } from '../../../../features/ropa/services/ropa.service';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../auth/services/auth.service';
import { RopaUtilsService } from '../../../../features/ropa/services/ropa-utils.service';
import { BasicDialogComponent } from '../../../../shared/components/basic-dialog/basic-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SimpleDialog } from '../../../../shared/models/simple-dialog';
import {
  ActionDropdownComponent,
  DropdownMenuOption,
} from '../../../../shared/components/action-dropdown/action-dropdown.component';
import { BulkImportService } from '../../../../features/ropa/services/bulk-import.service';
import { AzDocumentUpdateModalComponent } from '../../../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { StatusChangeModalComponent, ModalState } from '../../../../shared/components/az-status-change-modal/status-change-modal.component';

// Interface based on responses.ts structure
interface ROPAProject {
  id_project: number;
  ropa: string;
  project_title: string;
  set_area: string;
  countries?: string[];
  version: number;
  versions: number[];
  created_date: string;
  submitted_by: string;
  last_updated: string;
  has_active_draft: boolean;
  edited_by: string | null;
}

interface ROPAResponse {
  projects: ROPAProject[];
  total_count: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    ToolbarComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatIconModule,
    ActionDropdownComponent,
    AzDocumentUpdateModalComponent,
    StatusChangeModalComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  selectedTabIndex = 0;
  activeTab: 'all' | 'my' = 'all';
  allROPAs: ROPAProject[] = [];
  myROPAs: ROPAProject[] = [];
  filteredAllROPAs: ROPAProject[] = [];
  filteredMyROPAs: any[] = [];
  isLoading = false;
  error: string | null = null;
  isMobileOrTablet = false;
  prid = '';
  showSearchBar = false;
  searchQuery = '';
  showSortDropdown = false;
  currentSortOption: 'none' | 'set_area' | 'country' = 'none';

  constructor(
    private router: Router,
    private ropaService: RopaService,
    private _authService: AuthService,
    private ropaUtils: RopaUtilsService,
    readonly dialog: MatDialog,
    private bulkImportService: BulkImportService
  ) {
    this._authService.amplifyGetUserSession().forEach((session: any) => {
      this.prid = this.ropaUtils.extractPridFromSession(session);
    });
  }

  async ngOnInit() {
    this.detectDevice();
    await this.loadAllRopas();
  }

  private detectDevice() {
    const userAgent = navigator.userAgent;
    const screenWidth = window.innerWidth;

    this.isMobileOrTablet =
      screenWidth <= 1024 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
  }

  async loadAllRopas(project_id?: number, version?: number) {
    this.isLoading = true;
    this.error = null;

    try {
      const response: ROPAResponse = await this.ropaService.getAllRopas(
        project_id,
        version
      );
      this.allROPAs = response.projects;
      this.filterROPAs();
    } catch (error) {
      console.error('Error loading ROPAs:', error);
      this.error = 'Error loading ROPA data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async loadDrafts() {
    this.isLoading = true;
    this.error = null;

    try {
      const response: any = await this.ropaService.getAllDrafts(this.prid);
      this.myROPAs = response.drafts;
      this.filterROPAs();
    } catch (error) {
      console.error('Error loading drafts:', error);
      this.error = 'Error loading draft ROPA data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  switchTab(tab: 'all' | 'my') {
    this.activeTab = tab;
    if (tab === 'my') {
      this.loadDrafts();
    } else if (tab === 'all') {
      this.loadAllRopas();
    }
  }

  startNew() {
    this.router.navigate(['/ropa']);
  }

  handleExportROPAs() {
    const setAreasSet = new Set<string>();
    const countriesSet = new Set<string>();

    this.allROPAs.forEach(ropa => {
      if (ropa.set_area) {
        setAreasSet.add(ropa.set_area);
      }
      if (ropa.countries && Array.isArray(ropa.countries)) {
        ropa.countries.forEach(country => {
          if (country) {
            countriesSet.add(country);
          }
        });
      }
    });

    const uniqueSetAreas = Array.from(setAreasSet).sort((a, b) => a.localeCompare(b));
    const uniqueCountries = Array.from(countriesSet).sort((a, b) => a.localeCompare(b));

    const data: SimpleDialog = {
      uiTitle: `Export ROPA Entries`,
      uiMessage: `You can export all ROPA entries currently shown on the dashboard, or filter your export by Country and/or SET Area.`,
      showCancel: true,
      showConfirm: true,
      cancelText: 'Cancel',
      confirmText: 'Export CSV',
      dropdowns: {
        setAreas: uniqueSetAreas,
        countries: uniqueCountries
      }
    };

    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['submission-confirm-dialog', 'custom-dialog-border'],
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed) {
        this.exportROPAsToCSV(result.setArea, result.country);
      }
    });
  }

  async exportROPAsToCSV(setArea: string | null, country: string | null) {
    let ropasToExport = [...this.allROPAs];

    if (setArea) {
      ropasToExport = ropasToExport.filter(ropa => ropa.set_area === setArea);
    }

    if (country) {
      ropasToExport = ropasToExport.filter(ropa =>
        ropa.countries && ropa.countries.includes(country)
      );
    }

    this.exportModalState = 'processing';
    this.showExportModal = true;

    try {
      const response = await this.ropaService.exportROPAS(setArea || '', country || '');

      if (response && response.body !== undefined) {
        const apiResponse = response.body;

        if (apiResponse && apiResponse.body && apiResponse.isBase64Encoded) {
          this.downloadFileFromBase64(apiResponse.body, apiResponse.headers);
          this.exportModalState = 'success-with-message';
        } else {
          this.exportErrorMessage = 'Unexpected response from server. Please try again.';
          this.exportModalState = 'error';
        }
      } else {
        this.exportErrorMessage = 'Invalid response format. Please try again.';
        this.exportModalState = 'error';
      }
    } catch (error: any) {
      this.exportErrorMessage = error?.error?.message || error?.message || 'An error occurred while exporting. Please try again.';
      this.exportModalState = 'error';
    }
  }

  onExportModalClose(): void {
    this.showExportModal = false;
  }

  async downloadDraft(ropa: any) {
    if (!ropa) {
      console.warn('Cannot download: ROPA item is null or undefined');
      return;
    }
    try {
      const response = await this.ropaService.downloadDraft(
        ropa.draft_id,
        this.prid
      );

      if (response && response.body !== undefined) {
        const apiResponse = response.body;

        if (apiResponse && apiResponse.body && apiResponse.isBase64Encoded) {
          this.downloadFileFromBase64(
            apiResponse.body,
            apiResponse.headers,
            `Article 30 Report – ROPA-${ropa.draft_id}.pdf`
          );
        }
      } else if (response && typeof response === 'string') {
        // If response is direct base64 string
        const mockHeaders = {
          'Content-Type': 'application/pdf',
          'Content-Disposition':
            'attachment; filename="ropa-draft-download.pdf"',
        };
        this.downloadFileFromBase64(
          response,
          mockHeaders,
          `Article 30 Report – ROPA-${ropa.draft_id}.pdf`
        );
      } else {
        console.error(
          'Invalid response format for download. Response:',
          response
        );
        console.error(
          'Expected: {body: string, headers: object, isBase64Encoded: boolean}'
        );
      }
    } catch (error) {
      console.error('Error downloading ROPA:', error);
    }
  }

  async downloadRopa(ropa: ROPAProject) {
    if (!ropa) {
      console.warn('Cannot download: ROPA item is null or undefined');
      return;
    }

    try {
      const response = await this.ropaService.downloadRopa(
        ropa.id_project,
        ropa.version
      );
      console.log('Full download response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response || {}));

      // Handle HttpResponse structure (when using observe: 'response')
      if (response && response.body !== undefined) {
        console.log('HttpResponse detected');
        console.log('Response headers:', response.headers);
        console.log('Response body:', response.body);

        // The response.body should contain your API's response
        const apiResponse = response.body;

        if (apiResponse && apiResponse.body && apiResponse.isBase64Encoded) {
          console.log('Using HttpResponse -> API response structure');
          this.downloadFileFromBase64(
            apiResponse.body,
            apiResponse.headers,
            `Article 30 Report – ROPA-${ropa.id_project}.pdf`
          );
        } else {
          console.log('Unexpected HttpResponse body structure:', apiResponse);
        }
      } else if (response && typeof response === 'string') {
        console.log('Response is direct base64 string');
        // If response is direct base64 string
        const mockHeaders = {
          'Content-Type': 'application/pdf',
          'Content-Disposition': 'attachment; filename="ropa-download.pdf"',
        };
        this.downloadFileFromBase64(
          response,
          mockHeaders,
          `Article 30 Report – ROPA-${ropa.id_project}.pdf`
        );
      } else {
        console.error(
          'Invalid response format for download. Response:',
          response
        );
        console.error(
          'Expected: {body: string, headers: object, isBase64Encoded: boolean}'
        );
      }
    } catch (error) {
      console.error('Error downloading ROPA:', error);
    }
  }

  /**
   * Downloads a file from base64 encoded data
   */
  private downloadFileFromBase64(base64Data: string, headers: any, overrideFilename?: string) {
    try {
      console.log('Processing base64 data. Length:', base64Data?.length);
      console.log('Headers:', headers);

      if (!base64Data) {
        console.error('No base64 data provided');
        return;
      }

      // Decode base64 to binary
      const binaryString = atob(base64Data);
      console.log('Decoded binary string length:', binaryString.length);

      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob with the correct MIME type
      const mimeType = headers?.['Content-Type'] || 'application/pdf';
      const blob = new Blob([bytes], { type: mimeType });
      console.log('Created blob. Size:', blob.size, 'Type:', blob.type);

      // Extract filename from Content-Disposition header
      let filename: string;
      if (overrideFilename) {
        filename = overrideFilename;
      } else {
        filename = 'ropa-download.pdf'; // Default filename
        const contentDisposition = headers?.['Content-Disposition'];
        if (contentDisposition) {
          console.log('Content-Disposition:', contentDisposition);
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).?\2|[^;\n]*)/
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }
      console.log('Using filename:', filename);

      // Create download link and trigger download
      const downloadLink = document.createElement('a');
      const url = window.URL.createObjectURL(blob);

      downloadLink.href = url;
      downloadLink.download = filename;
      downloadLink.style.display = 'none';

      document.body.appendChild(downloadLink);
      console.log('Triggering download...');
      downloadLink.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(url);
        console.log('Cleanup completed');
      }, 100);

      console.log(`File ${filename} download initiated successfully`);
    } catch (error) {
      console.error('Error processing download:', error);
      console.error('Stack trace:', error);
    }
  }

  createNewVersion(ropa: ROPAProject) {
    if (!ropa) {
      console.warn('Cannot create new version: ROPA item is null or undefined');
      return;
    }
    if (ropa.has_active_draft) {
      this.conflictErrorMessage =
        'A new version of this ROPA is already in progress. You cannot create another version until the existing draft is published or deleted.';
      this.showConflictModal = true;
      return;
    }
    this.viewRopa(ropa, true);
  }

  async onVersionChange(ropa: ROPAProject) {
    if (!ropa) {
      console.warn(
        'Cannot process version change: ROPA item is null or undefined'
      );
      return;
    }

    try {
      // Llamar a loadAllRopas con los parámetros específicos
      const response: ROPAResponse = await this.ropaService.getAllRopas(
        ropa.id_project,
        ropa.version
      );

      // La respuesta es un array de un objeto, así que tomamos el primer elemento
      if (response.projects && response.projects.length > 0) {
        const updatedRopa = response.projects[0];

        // Encontrar el índice del proyecto actual en el array
        const index = this.allROPAs.findIndex(
          (r) => r.id_project === ropa.id_project
        );

        if (index !== -1) {
          // Reemplazar solo el proyecto específico, manteniendo los demás
          this.allROPAs[index] = { ...this.allROPAs[index], ...updatedRopa };

          // Update the filtered array to reflect the changes in the UI
          this.filterROPAs();
        }
      }
    } catch (error) {
      console.error('Error updating ROPA version:', error);
      this.error = 'Error updating ROPA version. Please try again.';
    }
  }

  viewRopa(ropa: ROPAProject, isEditMode: boolean) {
    if (!ropa) {
      console.warn('Cannot view: ROPA item is null or undefined');
      return;
    }
    this.router.navigate(['/ropa', ropa.id_project, ropa.version], {
      queryParams: { edit: isEditMode, ropaName: ropa.ropa },
    });
  }

  viewDraft(ropa: any, isEditMode: boolean) {
    if (!ropa) {
      console.warn('Cannot view: ROPA draft item is null or undefined');
      return;
    }
    this.router.navigate(['/ropa', ropa.draft_id], {
      queryParams: { edit: isEditMode, ropaName: ropa.ropa },
    });
  }

  editDraft(ropa: any) {
    if (!ropa) {
      console.warn('Cannot edit: ROPA draft item is null or undefined');
      return;
    }
    this.router.navigate(['/ropa', ropa.draft_id], {
      queryParams: { edit: true, ropaName: ropa.ropa },
    });
  }

  reassignAlert: string | null = null;
  showConflictModal = false;
  conflictErrorMessage = '';

  onConflictModalClose(): void {
    this.showConflictModal = false;
    this.conflictErrorMessage = '';
  }

  showExportModal = false;
  exportModalState: ModalState = 'processing';
  exportErrorMessage = '';
  showBulkImportModal = false;
  isBulkUploading = false;
  showBulkImportResultModal = false;
  bulkImportResultState: ModalState = 'processing';
  isDownloadingTemplate = false;
  showTemplateDownloadModal = false;
  templateDownloadModalState: ModalState = 'processing';

  private setTemplateDownloadLoading(loading: boolean): void {
    this.isDownloadingTemplate = loading;
    if (loading) {
      this.templateDownloadModalState = 'processing';
      this.showTemplateDownloadModal = true;
    }
    const opt = this.moreOptions.find(o => o.id === 'download-template');
    if (opt) opt.disabled = loading;
  }

  moreOptions: DropdownMenuOption[] = [
    { id: 'export-ropas', label: "Export ROPA's", icon: 'attach_file' },
    { id: 'bulk-action', label: 'Bulk Upload', icon: 'grid_on' },
    { id: 'download-template', label: 'Download template', icon: 'download' },
  ];

  onMoreOptionSelected(option: DropdownMenuOption): void {
    switch (option.id) {
      case 'export-ropas':
        this.handleExportROPAs();
        break;
      case 'bulk-action':
        this.openBulkImportModal();
        break;
      case 'download-template':
        this.downloadTemplate();
        break;
    }
  }

  openBulkImportModal(): void {
    this.showBulkImportModal = true;
  }

  closeBulkImportModal(): void {
    this.showBulkImportModal = false;
  }

  onBulkImportResultModalClose(): void {
    this.showBulkImportResultModal = false;
  }

  onBulkUploadConfirmed(payload: {
    file: File | null;
    documentType: string;
    comments: string;
  }): void {
    if (!payload.file) return;
    this.executeBulkUpload(payload.file);
  }

  private async executeBulkUpload(file: File): Promise<void> {
    this.isBulkUploading = true;
    try {
      const presignResponse = await this.bulkImportService.presignBulkUpload(file.name);
      await this.bulkImportService.uploadBulkToS3(file, presignResponse);
      this.showBulkImportModal = false;
      this.bulkImportResultState = 'success-with-message';
      this.showBulkImportResultModal = true;
    } catch {
      this.showBulkImportModal = false;
      this.bulkImportResultState = 'error';
      this.showBulkImportResultModal = true;
    } finally {
      this.isBulkUploading = false;
    }
  }

  async downloadTemplate(): Promise<void> {
    this.setTemplateDownloadLoading(true);
    try {
      const response = await this.ropaService.downloadTemplate();
      const apiResponse = response?.body;

      if (apiResponse?.isBase64Encoded && apiResponse?.body) {
        this.downloadFileFromBase64(apiResponse.body, apiResponse.headers);
        this.templateDownloadModalState = 'success-with-message';
      } else {
        this.templateDownloadModalState = 'error';
      }
    } catch {
      this.templateDownloadModalState = 'error';
    } finally {
      this.setTemplateDownloadLoading(false);
    }
  }

  onTemplateDownloadModalClose(): void {
    this.showTemplateDownloadModal = false;
  }

  reassignDraft(ropa: any) {
    const ropaName = ropa?.name || ropa?.draft_id || 'this draft';
    const data: SimpleDialog = {
      uiTitle: `Are you sure you want to reassign the "${ropaName}"?`,
      uiMessage:
        'Reassigning a ROPA draft transfers ownership and removes it from your "My ROPA" list. ' +
        'The assignee field accepts free text and is not automatically validated. ' +
        'Typographical errors, misspelled usernames, or selection of inactive users may result in failed delivery or loss of visibility. ' +
        'Please verify the assignee\'s exact username and active status before confirming.',
      textInput: {
        label: 'Assign to',
        placeholder: 'Example: abcd460@astrazeneca.net',
      },
      confirmText: 'Confirm',
      cancelText: 'Cancel',
    };

    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed && result.textValue) {
        const assignee = result.textValue;
        const confirmData: SimpleDialog = {
          uiTitle: 'Confirmation',
          uiMessage:
            `You are about to reassign this ROPA draft to "${assignee}". ` +
            'This action will remove the draft from your "My ROPA" and notify the assignee. ' +
            'Please confirm the assignee\'s identity and active status.',
          confirmationTags: [assignee],
          confirmText: 'Confirm',
          cancelText: 'Cancel',
        };

        const confirmDialogRef = this.dialog.open(BasicDialogComponent, {
          data: confirmData,
          width: '600px',
        });

        confirmDialogRef.afterClosed().subscribe((confirmResult) => {
          if (confirmResult) {
            const draftId = parseInt(ropa.draft_id);
            const previousOwnerEmail =
              this._authService.getAuthFromLocalStorage()?.payload?.email ?? '';
            this.ropaService
              .reassignDraft(draftId, assignee, previousOwnerEmail)
              .then(() => {
                this.reassignAlert = `ROPA draft reassigned to "${assignee}". Email notification sent.`;
                this.loadDrafts();
              })
              .catch((err) => {
                console.error('Reassign error:', err);

                const userMessage = 'Invalid email address: Please enter a valid email address in the format prid@astrazeneca.net';

                this.dialog.open(BasicDialogComponent, {
                  data: {
                    uiTitle: 'Reassign Failed',
                    uiMessage: userMessage,
                    error: { icon: 'error_outline' },
                    showCancel: false,
                    showConfirm: true,
                    confirmText: 'Close',
                  } as SimpleDialog,
                  width: '480px',
                  panelClass: ['custom-dialog-border'],
                });
              });
          }
        });
      }
    });
  }

  deleteDraft(ropa: any) {
    if (!ropa) {
      console.warn('Cannot delete: ROPA draft item is null or undefined');
      return;
    }

    const data: SimpleDialog = {
      uiTitle: `Are you sure you want to delete the draft "${this.getDraftRopaName(
        ropa
      )}"?`,
      uiMessage: `Are you sure you want to delete this file? It will be permanently removed from your ROPA list.`,
      showCancel: true,
      showConfirm: true,
      cancelText: 'Cancel',
      confirmText: 'Confirm',
    };

    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['submission-confirm-dialog', 'custom-dialog-border'],
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.ropaService
          .deleteDraft(parseInt(ropa.draft_id), this.prid)
          .then(() => {
            this.loadDrafts();
          })
          .catch((error) => {
            console.error('Error deleting draft:', error);
          });
      }
    });
  }

  publishDraft(ropa: any) {
    if (!ropa) {
      console.warn('Cannot delete: ROPA draft item is null or undefined');
      return;
    }

    const data: SimpleDialog = {
      uiTitle: `Confirm Publication of ROPA Entry`,
      uiMessage: `You are about to publish "${this.getDraftRopaName(
        ropa
      )}". Once published, this entry will move to the "All ROPA" list, making it visible and editable to users.`,
      showCancel: true,
      showConfirm: true,
      cancelText: 'Cancel',
      confirmText: 'Publish',
    };

    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['submission-confirm-dialog', 'custom-dialog-border'],
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.ropaService
          .publishDraft(ropa.ropa, this.prid, parseInt(ropa.draft_id))
          .then(() => {
            this.loadDrafts();
          })
          .catch((error) => {
            console.error('Error publishing draft:', error);
          });
      }
    });
  }

  getDraftRopaName(ropa: any): string {
    if (!ropa || !ropa.ropa) {
      return `ROPA-${ropa?.project_title || 'Untitled'}`;
    }
    return ropa.ropa;
  }

  getFormattedCountries(countries?: string[]): string {
    if (!countries || countries.length === 0) {
      return '-';
    }

    const formatCountryName = (country: string): string => {
      const withSpaces = country.replace(/_/g, ' ');
      return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };

    const sortedCountries = [...countries]
      .map(formatCountryName)
      .sort((a, b) => a.localeCompare(b));

    if (sortedCountries.length > 2) {
      return `${sortedCountries[0]}, ${sortedCountries[1]}...`;
    }

    return sortedCountries.join(', ');
  }

  toggleSearch() {
    this.showSearchBar = !this.showSearchBar;
    if (!this.showSearchBar) {
      this.searchQuery = '';
      this.filterROPAs();
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.filterROPAs();
  }

  onSearchChange() {
    this.filterROPAs();
  }

  toggleSort() {
    this.showSortDropdown = !this.showSortDropdown;
  }

  sortBy(option: 'set_area' | 'country') {
    this.currentSortOption = option;
    this.showSortDropdown = false;
    this.filterAndSortROPAs();
  }

  filterROPAs() {
    this.filterAndSortROPAs();
  }

  private filterAndSortROPAs() {
    const query = this.searchQuery.toLowerCase().trim();
    const normalizedQuery = query.replace(/[-\s]/g, '');

    if (!query) {
      this.filteredAllROPAs = [...this.allROPAs];
    } else {
      this.filteredAllROPAs = this.allROPAs.filter(
        (ropa) =>
          ropa.project_title?.toLowerCase().includes(query) ||
          (ropa.ropa?.toLowerCase().replace(/[-\s]/g, '')).includes(normalizedQuery) ||
          ropa.set_area?.toLowerCase().includes(query) ||
          ropa.submitted_by?.toLowerCase().includes(query)
      );
    }

    if (!query) {
      this.filteredMyROPAs = [...this.myROPAs];
    } else {
      this.filteredMyROPAs = this.myROPAs.filter(
        (ropa) =>
          ropa.project_title?.toLowerCase().includes(query) ||
          (ropa.ropa?.toLowerCase().replace(/[-\s]/g, '')).includes(normalizedQuery) ||
          ropa.set_area?.toLowerCase().includes(query)
      );
    }

    if (this.currentSortOption !== 'none') {
      this.applySorting();
    }
  }

  private applySorting() {
    if (this.currentSortOption === 'set_area') {
      this.filteredAllROPAs.sort((a, b) => {
        const valueA = (a.set_area || '').toLowerCase();
        const valueB = (b.set_area || '').toLowerCase();
        return valueA.localeCompare(valueB);
      });

      this.filteredMyROPAs.sort((a, b) => {
        const valueA = (a.set_area || '').toLowerCase();
        const valueB = (b.set_area || '').toLowerCase();
        return valueA.localeCompare(valueB);
      });
    } else if (this.currentSortOption === 'country') {
      this.filteredAllROPAs.sort((a, b) => {
        const valueA = (
          a.countries && a.countries.length > 0 ? a.countries[0] : ''
        ).toLowerCase();
        const valueB = (
          b.countries && b.countries.length > 0 ? b.countries[0] : ''
        ).toLowerCase();
        return valueA.localeCompare(valueB);
      });

      this.filteredMyROPAs.sort((a, b) => {
        const valueA = (
          a.countries && a.countries.length > 0 ? a.countries[0] : ''
        ).toLowerCase();
        const valueB = (
          b.countries && b.countries.length > 0 ? b.countries[0] : ''
        ).toLowerCase();
        return valueA.localeCompare(valueB);
      });
    }
  }
}
